import { useCallback, useEffect } from 'react';
import type React from 'react';
import _ from 'lodash';
import {
  fetchFacility,
  handleUpdateFacilityClinicianContacts,
  handleUpdateFacilityNote,
} from '@/entities/facility';
import { useUserStore } from '@/entities/user';
import { useShallow } from 'zustand/react/shallow';
import { INPUT_TYPE } from '@/shared/config';
import { useMergeState } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import { checkPhoneNumber, validateEmail } from '@/shared/lib/validation';
import type { FacilityInfoDrawerProps } from './facilityInfoDrawer';

type FacilityInfoDrawerState = {
  isScroll: boolean;
  data?: any;
  isOpenEditModal: boolean;
  facilityNote: string;
  originalFacilityNote: string;
  isSaving: boolean;
  selectedEditName?: string;
  clinicianContacts: any[];
  selectedClinicianContact?: any;
  isOpenConfirmDeleteClinicianContactModal: boolean;
  isUpdateClinicianContact?: boolean;
};

const useFacilityInfoDrawer = ({
  visible,
  facilityId,
  handleClickCloseButton,
  isAbortedStudy = false,
}: Pick<
  FacilityInfoDrawerProps,
  'visible' | 'facilityId' | 'handleClickCloseButton' | 'isAbortedStudy'
>) => {
  const [state, setState] = useMergeState<FacilityInfoDrawerState>({
    isScroll: false,
    data: undefined,
    isOpenEditModal: false,
    facilityNote: '',
    originalFacilityNote: '',
    isSaving: false,
    selectedEditName: undefined,
    clinicianContacts: [],
    selectedClinicianContact: undefined,
    isOpenConfirmDeleteClinicianContactModal: false,
  });

  const { roles = [], rolePermissions = {} } = useUserStore(
    useShallow((s) => ({
      roles: s.me?.roles,
      rolePermissions: s.me?.group?.rolePermissions,
    })),
  );

  const editFacilityNoteRoles = Array.isArray(rolePermissions?.editFacilityNotes)
    ? rolePermissions.editFacilityNotes
    : [];

  const canEditFacilityNote = !!_.intersection(roles, editFacilityNoteRoles)[0];

  /**
   * Opens or closes the facility edit modal and primes it for the item being edited. Closing restores the facility note from its original copy; opening on Clinic Support Contacts deep-clones the contact list, picks the row matching the given index or an empty object when adding, resets its error map and records whether this is an update rather than a create.
   *
   * @param selectedEditName - Label of the facility item to edit, such as Clinic Support Contacts.
   * @param clinicianContacts - Current clinician contact rows, cloned before editing.
   * @param index - Index of the contact being updated; omitted when adding a new one.
   * @returns Nothing.
   */
  const toggleDisplayEditModal = (
    selectedEditName?: string,
    clinicianContacts?: any[],
    index?: number,
  ) => {
    const updatedIsOpenEditModal = !state.isOpenEditModal;
    const setStateObject: Partial<FacilityInfoDrawerState> = {
      isOpenEditModal: updatedIsOpenEditModal,
      selectedEditName,
    };

    if (!updatedIsOpenEditModal) {
      _.assign(setStateObject, {
        facilityNote: state.originalFacilityNote,
      });
    }

    if (selectedEditName === 'Clinic Support Contacts') {
      const clonedClinicianContacts = _.cloneDeep(clinicianContacts);
      const isUpdateClinicianContact = !_.isNil(index);
      const selectedClinicianContact = isUpdateClinicianContact
        ? _.find(clonedClinicianContacts, (x) => x.index === index)
        : {};
      _.assign(selectedClinicianContact, { errorObject: {} });
      _.assign(setStateObject, {
        selectedClinicianContact,
        isUpdateClinicianContact,
      });
    }
    setState(setStateObject);
  };

  /**
   * Lays out the facility info drawer as four titled sections from a fetched facility record. Emits read-only Contact Information and Sales Rep Contact blocks built from the facility's friendly id, name, phone, fax, joined address and assigned sales representative, plus editable Clinic Support Contacts and Facility Notes sections whose edit affordances open the edit modal and which are locked when the study is aborted or the user cannot edit facility notes.
   *
   * @param facilityInfo - Facility record supplying contact, clinician, sales-rep and note data.
   * @param isAbortedStudy - When true, marks the editable sections as read-only.
   * @returns An array of drawer section descriptors, each with a header, content and optional edit handler.
   */
  const generateFacilityInfoData = (facilityInfo: any, isAbortedStudy?: boolean) => [
    {
      header: 'Contact Information',
      content: [
        {
          label: 'Facility ID',
          value: facilityInfo?.friendlyId,
        },
        {
          label: 'Facility Name',
          value: facilityInfo?.name,
        },
        {
          label: 'Phone Number',
          value: facilityInfo?.contact?.phone1,
        },
        {
          label: 'Fax Number',
          value: facilityInfo?.contact?.fax,
        },
        {
          label: 'Address',
          value: `${facilityInfo?.contact?.address}, ${facilityInfo?.contact?.city}, ${facilityInfo?.contact?.state}, ${facilityInfo?.contact?.zip}`,
        },
      ],
    },
    {
      header: 'Clinic Support Contacts',
      handleClickEdit: (clinicianContacts: any[], index?: number) => {
        toggleDisplayEditModal('Clinic Support Contacts', clinicianContacts, index);
      },
      content: facilityInfo?.clinicianContacts,
      isNotEdit: isAbortedStudy,
    },
    {
      header: 'Sales Rep Contact',
      content: [
        {
          label: 'Name',
          value: !_.isEmpty(facilityInfo?.assignedSalesRepresentative)
            ? `${facilityInfo?.assignedSalesRepresentative?.firstName} ${facilityInfo?.assignedSalesRepresentative?.lastName}`
            : '',
        },
        {
          label: 'Phone Number',
          value: !_.isEmpty(facilityInfo?.assignedSalesRepresentative)
            ? `${facilityInfo?.assignedSalesRepresentative?.contact?.phone1 || '--'}`
            : '',
        },
        {
          label: 'Email',
          value: !_.isEmpty(facilityInfo?.assignedSalesRepresentative)
            ? `${facilityInfo?.assignedSalesRepresentative?.email || '--'}`
            : '',
        },
      ],
    },
    {
      header: 'Facility Notes',
      handleClickEdit: () => {
        toggleDisplayEditModal('Facility Notes');
      },
      content: facilityInfo?.facilityNote,
      isNotEdit: isAbortedStudy || !canEditFacilityNote,
    },
  ];

  /**
   * Persists the edited facility note for the facility shown in the drawer. Sets a saving flag, sends the note to the server, then deep-clones the drawer sections and writes the saved note onto the Facility Notes section, storing it as both draft and original before closing the modal; a failure logs, toasts and just clears the saving flag.
   *
   * @returns A promise resolving once the facility note has been saved and the drawer refreshed.
   */
  const updateFacilityNote = async () => {
    try {
      setState({ isSaving: true });
      const facilityNote = await handleUpdateFacilityNote(facilityId, state.facilityNote);
      const dataClone = _.cloneDeep(state.data);
      const facilityNoteData = _.find(dataClone, (x: any) => x.header === 'Facility Notes');
      if (facilityNoteData) {
        facilityNoteData.content = facilityNote;
      }
      setState({
        data: dataClone,
        facilityNote,
        originalFacilityNote: facilityNote,
        isOpenEditModal: false,
        isSaving: false,
      });
    } catch (error: any) {
      console.error('Failed to update facility note: ', error);
      toastrError(error.message, 'Error');
      setState({ isSaving: false });
    }
  };

  /**
   * Normalizes raw clinic support contacts into the editable rows the facility drawer renders. Maps each contact to its position index plus name, phone, email and description, and seeds an empty errorObject so per-field validation messages have somewhere to land.
   *
   * @param clinicianContacts - Raw clinician contact records returned for the facility.
   * @returns An array of editable clinician-contact rows carrying an index and an empty error map.
   */
  const clinicianContactsFormatter = (clinicianContacts: any[]) =>
    _.map(clinicianContacts, (clinicianContact: any, index: number) => ({
      index,
      name: clinicianContact.name,
      phone: clinicianContact.phone,
      email: clinicianContact.email,
      description: clinicianContact.description,
      errorObject: {},
    }));

  /**
   * Validates the clinician contact currently open in the edit modal. Collects an Invalid value message for a phone that fails the phone-number check and for an email that fails the email check, plus a maximum-characters message when the description exceeds 100 characters.
   *
   * @param selectedClinicianContact - Clinician contact whose phone, email and description are validated.
   * @returns A map of field name to validation message, empty when the contact is valid.
   */
  const checkClinicianContact = (selectedClinicianContact: any) => {
    const errorObject: Record<string, string> = {};
    if (!checkPhoneNumber(selectedClinicianContact.phone)) {
      _.assign(errorObject, { phone: 'Invalid value' });
    }
    if (!validateEmail(selectedClinicianContact.email)) {
      _.assign(errorObject, { email: 'Invalid value' });
    }
    if (selectedClinicianContact.description?.length > 100) {
      _.assign(errorObject, { description: 'You have entered the maximum 100 characters limit' });
    }
    return errorObject;
  };

  /**
   * Commits an add, update or delete of a clinic support contact to the facility. Copies the contact rows, removes the selected one when deleting or otherwise merges the edited fields into the matching row or appends it, strips the local index and errorObject helpers, sends the list to the server and writes the returned contacts back into the Clinic Support Contacts section while clearing the edit and delete-confirmation modals; a failure logs, toasts and just clears the saving flag.
   *
   * @param isDelete - When true, removes the selected contact instead of saving it.
   * @returns A promise resolving once the contacts have been persisted and the drawer refreshed.
   */
  const updateFacilityClinicianContacts = async (isDelete = false) => {
    try {
      setState({ isSaving: true });
      const updatedClinicianContacts = [...state.clinicianContacts];
      if (isDelete) {
        _.remove(updatedClinicianContacts, (x) => x.index === state.selectedClinicianContact.index);
      } else {
        const updatedElement = _.find(
          updatedClinicianContacts,
          (x) => x.index === state.selectedClinicianContact.index,
        );
        if (updatedElement) {
          _.assign(updatedElement, { ...state.selectedClinicianContact });
        } else {
          updatedClinicianContacts.push(state.selectedClinicianContact);
        }
      }
      _.forEach(updatedClinicianContacts, (item) => {
        delete item.index;
        delete item.errorObject;
      });
      const afterUpdatingClinicianContacts = await handleUpdateFacilityClinicianContacts(
        facilityId,
        updatedClinicianContacts,
      );
      const dataClone = _.cloneDeep(state.data);
      const facilityClinicianContactData = _.find(
        dataClone,
        (x: any) => x.header === 'Clinic Support Contacts',
      );
      if (facilityClinicianContactData) {
        facilityClinicianContactData.content = afterUpdatingClinicianContacts;
      }
      setState({
        selectedClinicianContact: undefined,
        data: dataClone,
        isOpenEditModal: false,
        isSaving: false,
        clinicianContacts: [...clinicianContactsFormatter(afterUpdatingClinicianContacts)],
        isOpenConfirmDeleteClinicianContactModal: false,
      });
    } catch (error: any) {
      console.error('Failed to update clinic support contacts: ', error);
      toastrError(error.message, 'Error');
      setState({ isSaving: false });
    }
  };

  /**
   * Handles the save press on the clinician-contact form. Runs the phone, email and description checks and, when any fail, attaches the resulting messages to the selected contact so the fields render their errors, otherwise it hands off to the contact-persisting routine.
   *
   * @returns Nothing.
   */
  const onClickSaveClinicianContact = () => {
    const errorObject = checkClinicianContact(state.selectedClinicianContact);
    if (!_.isEmpty(errorObject)) {
      _.assign(state.selectedClinicianContact, { errorObject });
      setState({ selectedClinicianContact: state.selectedClinicianContact });
    } else {
      updateFacilityClinicianContacts();
    }
  };

  /**
   * Shows or hides the confirmation prompt for deleting a clinic support contact. Inverts the isOpenConfirmDeleteClinicianContactModal flag in local state.
   *
   * @returns Nothing.
   */
  const toggleDisplayConfirmDeleteClinicianContactModal = () => {
    setState({
      isOpenConfirmDeleteClinicianContactModal: !state.isOpenConfirmDeleteClinicianContactModal,
    });
  };

  /**
   * Loads the facility behind the current study into the drawer. Fetches the facility by id, lays it out into drawer sections honouring the aborted-study lock, and stores the sections along with the facility note as both draft and original plus the clinician contacts normalized into editable rows; a failure only raises an error toast.
   *
   * @returns A promise resolving once the facility sections and notes are in state.
   */
  const fetchData = async () => {
    try {
      const facility = await fetchFacility(facilityId);
      const data = generateFacilityInfoData(facility, isAbortedStudy);
      setState({
        data,
        facilityNote: (facility as any)?.facilityNote,
        originalFacilityNote: (facility as any)?.facilityNote,
        clinicianContacts: [...clinicianContactsFormatter((facility as any)?.clinicianContacts)],
      });
    } catch (error: any) {
      toastrError(error.message, 'Error');
    }
  };

  /**
   * Tracks whether the facility info drawer body has been scrolled away from the top so the header can render its shadow. Reads scrollTop off the scrolled div and flips the isScroll flag in local state only when the top or not-top condition disagrees with the flag already stored.
   *
   * @param e - Scroll event from the drawer's scrollable container.
   * @returns Nothing.
   */
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (e?.target) {
      const { scrollTop } = e.target as HTMLDivElement;
      if (scrollTop === 0 && state.isScroll) {
        setState({ isScroll: false });
      } else if (scrollTop !== 0 && !state.isScroll) {
        setState({ isScroll: true });
      }
    }
  };

  /**
   * Records keystrokes in the facility-note editor as the user types. Writes the incoming value into the facilityNote field of local state and ignores the field name, since the modal edits only that one field.
   *
   * @param name - Name of the edited field; unused because only the facility note is editable.
   * @param value - Current text of the facility-note editor.
   * @returns Nothing.
   */
  const onChangeFacilityNote = (name: string, value: string) => {
    setState({ facilityNote: value });
  };

  /**
   * Builds the field descriptors for whichever facility item the edit modal currently targets. For Clinic Support Contacts it emits optional name, phone with number masking, email and a description textarea, each bound to the selected contact and its per-field error, otherwise a single facilityNote field; memoized on the note, the selected item name and the contact list.
   *
   * @returns An array of input descriptors for the clinician-contact form or the facility-note field.
   */
  const generateInputList = useCallback(
    () =>
      state.selectedEditName === 'Clinic Support Contacts'
        ? [
            {
              name: 'name',
              label: 'Name (optional)',
              value: state.selectedClinicianContact?.name || '',
              type: INPUT_TYPE.TEXT_INPUT,
              className: 'w-100',
              placeholder: 'Enter clinician name',
            },
            {
              name: 'phone',
              label: 'Phone number',
              value: state.selectedClinicianContact?.phone || '',
              type: INPUT_TYPE.TEXT_INPUT,
              className: 'w-100',
              placeholder: '800-555-1234',
              isPhoneNumberMask: true,
              error: state.selectedClinicianContact?.errorObject?.phone,
            },
            {
              name: 'email',
              label: 'Email',
              value: state.selectedClinicianContact?.email || '',
              type: INPUT_TYPE.TEXT_INPUT,
              className: 'w-100',
              placeholder: 'johnsmith@mail.com',
              error: state.selectedClinicianContact?.errorObject?.email,
            },
            {
              name: 'description',
              label: 'Description (optional)',
              value: state.selectedClinicianContact?.description || '',
              type: INPUT_TYPE.TEXT_AREA_INPUT,
              className: 'w-100',
              placeholder: 'Enter description',
              error: state.selectedClinicianContact?.errorObject?.description,
            },
          ]
        : [
            {
              name: 'facilityNote',
              value: state.facilityNote,
            },
          ],
    [state.facilityNote, state.selectedEditName, state.clinicianContacts],
  );

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible, facilityId]);

  /**
   * Applies an edit to one field of the clinician contact open in the modal, enforcing the description length limit. A description over 100 characters records a maximum-characters message in the contact's errorObject and leaves the value untouched, otherwise the description error is cleared and the named field is written onto the selected contact before state is refreshed.
   *
   * @param name - Name of the clinician-contact field being edited.
   * @param value - New value entered for that field.
   * @returns Nothing.
   */
  const onChangeClinicianContact = (name: string, value: any) => {
    const clonedErrorObject = { ...state.selectedClinicianContact.errorObject };
    if (name === 'description' && value?.length > 100) {
      _.merge(clonedErrorObject, {
        description: 'You have entered the maximum 100 characters limit',
      });
      _.assign(state.selectedClinicianContact, { errorObject: clonedErrorObject });
      setState({ selectedClinicianContact: state.selectedClinicianContact });
      return;
    }
    delete clonedErrorObject.description;
    _.assign(state.selectedClinicianContact, { errorObject: clonedErrorObject });
    state.selectedClinicianContact[name] = value;
    setState({ selectedClinicianContact: state.selectedClinicianContact });
  };

  return {
    state,
    toggleDisplayEditModal,
    updateFacilityNote,
    onClickSaveClinicianContact,
    toggleDisplayConfirmDeleteClinicianContactModal,
    updateFacilityClinicianContacts,
    handleScroll,
    onChangeFacilityNote,
    generateInputList,
    onChangeClinicianContact,
  };
};

export default useFacilityInfoDrawer;
